import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import BottomMobileOptionsModal from "@/components/bottom-mobile-options-modal";
import CloudinaryImage from "@/components/cloudinary-image";
import CommunityScoreWheel from "@/components/community-score-wheel";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { gameUrl } from "@/helpers/games";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { cn } from "@/lib/utils";

import {
  AwardsIcon,
  CouponsIcon,
  FlashIcon,
  SavedIcon,
  SponsoredIcon,
} from "./icons";
import type { PromotedGameType } from "./types";

export type { PromotedGameType } from "./types";

function SponsoredCopy() {
  return (
    <p className="text-14/[1.72em] text-heather">
      These ads are for Partner Program members.{" "}
      <a
        className="text-rose-red"
        href="https://link.morty.app/e/sponsored"
        rel="nofollow noopener"
        target="_blank"
      >
        Learn more.
      </a>
    </p>
  );
}

function SponsoredLabel() {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useIsBreakPoint("md");

  return (
    <>
      <HoverCard onOpenChange={setIsOpen} open={isOpen}>
        <HoverCardTrigger className="absolute top-0 right-0 z-1 cursor-help p-3 hover:[&>svg]:text-white/20">
          <SponsoredIcon className="transition-colors" />
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-medium text-12 text-white">
            Sponsored
          </span>
        </HoverCardTrigger>
        {isDesktop && (
          <HoverCardContent className="rounded-[10px] bg-deep-sea px-5 shadow-none">
            <h6 className="border-white/10 border-b py-[15px] text-center font-semibold text-16/[1.5em] text-white">
              Sponsored listing
            </h6>
            <div className="pt-[15px] pb-[9px]">
              <SponsoredCopy />
            </div>
          </HoverCardContent>
        )}
      </HoverCard>
      {!isDesktop && (
        <BottomMobileOptionsModal
          onOpenChange={setIsOpen}
          open={isOpen}
          title="Sponsored listing"
        >
          <SponsoredCopy />
        </BottomMobileOptionsModal>
      )}
    </>
  );
}

interface GameIconsStackProps {
  game: PromotedGameType;
}

function GameIconsStack({ game }: GameIconsStackProps) {
  const icons = useMemo(() => {
    const i: React.ReactNode[] = [];

    if (game.isSaved) {
      i.push(<SavedIcon />);
    }
    if (game.hasAwards) {
      i.push(<AwardsIcon />);
    }

    return i;
  }, [game]);

  if (!icons.length) {
    return null;
  }

  return (
    <div className="absolute top-3 left-3.5 -z-1 flex gap-2.5">
      {icons.map((icon, index) => (
        <div
          className="flex size-7.5 items-center justify-center rounded-[3px] bg-black-russian-dark"
          // biome-ignore lint/suspicious/noArrayIndexKey: legacy
          key={index}
        >
          {icon}
        </div>
      ))}
    </div>
  );
}

interface PromotedGameTileProps extends React.ComponentProps<"div"> {
  game: PromotedGameType;
}

export default function PromotedGameTile({
  className,
  game,
  ...props
}: PromotedGameTileProps) {
  const isDesktop = useIsBreakPoint("md");

  return (
    <div
      className={cn(
        "group relative flex aspect-[290/168] items-end justify-stretch px-3 py-3.5",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "absolute inset-0 -z-1 overflow-hidden rounded-[10px] shadow-white/25 transition-shadow",
          'before:absolute before:inset-0 before:bg-linear-[180deg,#00000080_10%,#00000000_25%,#00000000_37%,#00000080_64%] before:content-[""]',
          "group-hover:md:shadow-0-0-20"
        )}
      >
        <CloudinaryImage className="size-full" game={game} rW={282 * 2} />
      </div>
      <GameIconsStack game={game} />
      {game.isPromoted && <SponsoredLabel />}
      <div>
        <h4 className="font-medium text-18/[1.5em] text-white">
          <Link
            className={cn(
              "link-overlay",
              game.hasBookings &&
                "bg-linear-44 from-tangerine to-bright-pink bg-clip-text text-transparent"
            )}
            target={isDesktop ? "_blank" : undefined}
            to={gameUrl(game)}
          >
            {game.name}
            {game.hasBookings && <FlashIcon className="inline" />}
            {!!(game.coupons || []).length && (
              <CouponsIcon className="inline" />
            )}
          </Link>
        </h4>
        <h6 className="mt-0.5 font-normal text-12/[1.2em] text-white/80">
          {game.location.displayName || game.location.company.name}
        </h6>
        {(game.communityRatingCount || 0) > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <CommunityScoreWheel
              className="size-4"
              scores={game.communityScorePercentages || []}
            />
            <p className="font-medium text-12 text-white">
              {game.communityScoreBucket}
            </p>
            <p className="text-12 text-cool-gray">
              ({game.communityRatingCount} rating
              {game.communityRatingCount === 1 ? "" : "s"})
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
